import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import './RdOverlay.css';

const RdOverlaySvg = ({ visible }) => {
  const overlayRef = useRef(null);
  const svgRef = useRef(null);
  
  useEffect(() => {
    if (!visible || !overlayRef.current) return;
    
    // Continuous animation that never resets
    const ctx = gsap.context(() => {
      // Set initial position
      gsap.set(overlayRef.current, { "--h": "0%" });
      
      // Create a truly continuous animation that repeats forever
      gsap.to(overlayRef.current, {
        "--h": "100%",
        duration: 15,
        repeat: -1,
        ease: "none", // Linear for smooth continuous movement
        onRepeat: () => {
          // Immediately reset to 0% without any visible change
          gsap.set(overlayRef.current, { "--h": "0%" });
        }
      });
      
      // Pulsing opacity with yoyo effect for smooth transitions
      gsap.to(overlayRef.current, {
        "--o": 0.7,
        duration: 4,
        repeat: -1,
        yoyo: true, // Smoothly reverse the animation
        ease: "sine.inOut"
      });
    });
    
    // Cleanup function
    return () => ctx.revert();
  }, [visible]);
  
  if (!visible) return null;
  
  // Generate a more sparse RD text pattern
  const generateRdPatternElements = () => {
    const elements = [];
    // Reduced number of rows and columns
    const rows = 4;
    const cols = 4;
    // Increased spacing between elements
    const spacing = 160;
    
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        elements.push(
          <text 
            key={`rd-${i}-${j}`}
            x={j * spacing + ((i % 2) * spacing/2)} 
            y={i * spacing + 20}
            style={{
              fontFamily: 'LovedHei',
              fontWeight: 'bold',
              fontSize: '128px',
              fill: 'white',
              opacity: 0.8,
              transform: 'rotate(45deg)',
              transformOrigin: `${j * spacing + ((i % 2) * spacing/2)}px ${i * spacing + 20}px`
            }}
          >
            RD
          </text>
        );
      }
    }
    
    return elements;
  };
  
  return (
    <div 
      ref={overlayRef} 
    //   className="absolute inset-0 pointer-events-none z-10 rounded-lg overflow-hidden"
      style={{
        "--h": "0%",
        "--o": 0.5,
      }}
    >
      <div 
        className="rd-gradient-bg absolute inset-0"
        style={{
          // Create a tileable gradient that repeats seamlessly
          backgroundImage: `linear-gradient(
            -45deg,
            rgba(255, 255, 255, 0.3) 0%,
            rgba(200, 200, 200, 0.6) 25%,
            rgba(255, 107, 1, 0.8) 50%,
            rgba(200, 200, 200, 0.6) 75%,
            rgba(255, 255, 255, 0.3) 100%
          )`,
          backgroundSize: "200% 200%",
          backgroundPosition: `var(--h) var(--h)`,
          backgroundRepeat: "repeat", // Set to repeat for seamless tiling
          opacity: "var(--o)",
        }}
      >
        <svg 
          ref={svgRef}
          width="100%" 
          height="100%" 
          viewBox="0 0 800 500" 
          preserveAspectRatio="xMidYMid slice"
          style={{
            position: 'absolute',
            inset: 0,
          }}
        >
          <defs>
            <linearGradient id="silverGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
              <stop offset="50%" stopColor="#d0d0d0" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#f5f5f5" stopOpacity="0.9" />
            </linearGradient>
          </defs>
          <mask id="rdMask">
            <rect width="100%" height="100%" fill="black" />
            {generateRdPatternElements().map(el => {
              // Clone the element but change fill to white for mask
              return React.cloneElement(el, {
                style: { ...el.props.style, fill: 'white' }
              });
            })}
          </mask>
          <rect width="100%" height="100%" fill="url(#silverGradient)" mask="url(#rdMask)" />
        </svg>
      </div>
    </div>
  );
};

export default RdOverlaySvg;